/* ----------------------------------------------------------------------------
   LogicalName — the merchant-facing "courier code" from the Courier Code Display
   BRD (Oct 2026), e.g. DELHIVERY_FWD_SURFACE_REG_0.5KG. Derived on the backend
   from courier attributes (the segment list is the backend enum's business);
   null until every required attribute is filled. Several account codes share one
   logical name by design and `priority` (1 = picked first) ranks them.
   Phase 1 = visibility only.
   ---------------------------------------------------------------------------- */
import React from 'react'
import PropTypes from 'prop-types'

const MONO = "'JetBrains Mono', ui-monospace, SFMono-Regular, Menlo, monospace"

/* Kept generic on purpose: the attribute list lives in the backend naming rule (CourierLogicalNameSegment)
   and is returned by the Resync result, so the UI never hardcodes it. */
export const NOT_NAMED_HINT =
  'Not named yet — a required naming attribute is missing on the code. Fill it and save, or run Resync Logical Names to see which attributes the rule needs.'

/** Inline tag: name in mono on a light indigo ground, or a dashed amber "Not named" pill. */
export default function LogicalName({value, priority, size = 'sm', showPriority = true}) {
  const fs = size === 'lg' ? 13 : 11.5
  if (!value) {
    return (
      <span
        title={NOT_NAMED_HINT}
        style={{
          display: 'inline-block',
          fontSize: fs - 0.5,
          fontWeight: 600,
          color: '#C2410C',
          background: '#FFF7ED',
          border: '1px dashed #FDBA74',
          borderRadius: 4,
          padding: '2px 7px',
          whiteSpace: 'nowrap',
        }}
      >
        Not named
      </span>
    )
  }
  return (
    <span style={{display: 'inline-flex', alignItems: 'center', gap: 6, minWidth: 0}}>
      <span
        title={value}
        style={{
          fontFamily: MONO,
          fontSize: fs,
          fontWeight: 600,
          color: '#3730A3',
          background: '#EEF2FF',
          border: '1px solid #C7D2FE',
          borderRadius: 4,
          padding: '2px 7px',
          letterSpacing: 0.2,
          overflow: 'hidden',
          textOverflow: 'ellipsis',
          whiteSpace: 'nowrap',
          maxWidth: '100%',
        }}
      >
        {value}
      </span>
      {showPriority && priority != null ? (
        <span
          title={'Priority ' + priority + ' among account codes sharing this logical name (1 = picked first).'}
          style={{
            fontSize: 10.5,
            fontWeight: 700,
            color: '#1565C0',
            background: '#E3F2FD',
            borderRadius: 4,
            padding: '1px 6px',
            whiteSpace: 'nowrap',
          }}
        >
          P{priority}
        </span>
      ) : null}
    </span>
  )
}

LogicalName.propTypes = {
  value: PropTypes.string,
  priority: PropTypes.number,
  size: PropTypes.oneOf(['sm', 'lg']),
  showPriority: PropTypes.bool,
}
